import Axios, { AxiosRequestConfig, Method } from "axios";

// Use VITE_API_URL for Cloud Run deployment, fallback to /api for local dev
const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

export const AXIOS_INSTANCE = Axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Send cookies with all requests
});

/**
 * Mutator for orval-generated client.
 *
 * Orval (8.x with the default fetch-style template) calls the mutator with:
 *   queryClient<T>(url: string, options: RequestInit)
 *
 * We translate that into an axios request, parsing JSON bodies and forwarding
 * headers and abort signals.
 */
export const apiMutator = <T>(
  url: string,
  options?: RequestInit,
): Promise<T> => {
  const source = Axios.CancelToken.source();

  // Forward fetch's AbortSignal as an axios cancellation
  if (options?.signal) {
    options.signal.addEventListener("abort", () => source.cancel("Aborted"));
  }

  // Translate fetch headers (which can be Headers, [string,string][], or Record)
  // into a plain Record that axios accepts.
  const headers: Record<string, string> = {};
  if (options?.headers) {
    if (options.headers instanceof Headers) {
      options.headers.forEach((value, key) => {
        headers[key] = value;
      });
    } else if (Array.isArray(options.headers)) {
      for (const [key, value] of options.headers) {
        headers[key] = value;
      }
    } else {
      Object.assign(headers, options.headers as Record<string, string>);
    }
  }

  // Parse JSON bodies; pass through other body types (FormData, Blob, etc.)
  let data: unknown = options?.body;
  const contentType = headers["Content-Type"] || headers["content-type"];
  if (typeof options?.body === "string" && contentType?.includes("application/json")) {
    try {
      data = JSON.parse(options.body);
    } catch {
      // Leave as string if parse fails
    }
  }

  const config: AxiosRequestConfig = {
    url,
    method: (options?.method ?? "GET") as Method,
    headers,
    data,
    cancelToken: source.token,
  };

  const promise = AXIOS_INSTANCE(config).then(({ data: responseData }) => responseData);

  // @ts-expect-error - Adding cancel method to promise
  promise.cancel = () => {
    source.cancel("Query was cancelled");
  };

  return promise;
};

export default apiMutator;
